import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, AlertTriangle, Calendar, Trash2 } from "lucide-react";

const BUCKET_LABEL = {
  in_pipeline: { label: "In pipeline", color: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  overdue: { label: "Overdue", color: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  due_30: { label: "Due in 30 days", color: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  due_60: { label: "Due in 60 days", color: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-400" },
  due_90: { label: "Due in 90 days", color: "bg-blue-50 text-blue-700 border-blue-100", dot: "bg-blue-500" },
  later: { label: "Later", color: "bg-slate-50 text-slate-600 border-slate-200", dot: "bg-slate-400" },
};

export default function Renewals() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(180);
  const [bucket, setBucket] = useState("all");
  const [includeHistorical, setIncludeHistorical] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const r = await api.get("/renewals", { params: { days_ahead: days, days_back: includeHistorical ? 99999 : 180 } });
    setData(r.data);
    setSelected(new Set());
    setLoading(false);
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const visibleIds = (filtered || []).map((d) => d.id);
    const allSelected = visibleIds.every((id) => selected.has(id)) && visibleIds.length > 0;
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const bulkDelete = async () => {
    if (!isAdmin) { toast.error("Only admins can delete deals"); return; }
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} renewal deal${ids.length === 1 ? "" : "s"}? This hard-deletes them from the accounts too — it can't be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/deals/bulk-delete", { deal_ids: ids });
      toast.success(`Deleted ${r.data?.deleted ?? ids.length} renewal${(r.data?.deleted ?? ids.length) === 1 ? "" : "s"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const removeRenewal = async (d, e) => {
    e.stopPropagation();
    if (!isAdmin) {
      toast.error("Only admins can delete deals");
      return;
    }
    if (!window.confirm(`Delete "${d.name}" from ${d.account_name}? This hard-deletes the deal from the account too — it can't be undone.`)) return;
    setDeletingId(d.id);
    try {
      await api.delete(`/deals/${d.id}`);
      toast.success("Renewal deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, includeHistorical]);

  const items = data?.items || [];
  const filtered = bucket === "all" ? items : items.filter((i) => i.bucket === bucket);
  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="renewals-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Retention</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Renewals due</h1>
          <p className="text-slate-500 text-sm mt-1">
            Every open renewal deal in pipeline, plus Closed Won deals approaching their next renewal cycle (close date + term length).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-44" data-testid="renewals-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Next 30 days</SelectItem>
              <SelectItem value="60">Next 60 days</SelectItem>
              <SelectItem value="90">Next 90 days</SelectItem>
              <SelectItem value="180">Next 6 months</SelectItem>
              <SelectItem value="365">Next 12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={includeHistorical ? "default" : "outline"}
            size="sm"
            onClick={() => setIncludeHistorical(!includeHistorical)}
            data-testid="renewals-include-historical"
          >
            {includeHistorical ? "Including history ✓" : "Include history"}
          </Button>
          <Button variant="outline" size="icon" onClick={load} data-testid="renewals-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <SummaryCard label="MRR at risk" value={fmtMoney(data?.mrr_at_risk || 0)} accent="text-red-700" testid="kpi-mrr-at-risk" />
        <SummaryCard label="In pipeline" value={summary.in_pipeline || 0} accent="text-indigo-700" testid="kpi-in-pipeline" />
        <SummaryCard label="Overdue" value={summary.overdue || 0} accent="text-red-700" />
        <SummaryCard label="Due 30d" value={summary.due_30 || 0} accent="text-amber-700" />
        <SummaryCard label="Due 60d" value={summary.due_60 || 0} accent="text-amber-600" />
        <SummaryCard label="Due 90d" value={summary.due_90 || 0} accent="text-blue-700" />
      </div>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" /> Upcoming renewals
          </CardTitle>
          <div className="flex items-center gap-2">
            {isAdmin && selected.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={bulkDelete}
                disabled={bulkDeleting}
                data-testid="renewals-bulk-delete-btn"
                className="h-8"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                {bulkDeleting ? "Deleting…" : `Delete ${selected.size}`}
              </Button>
            )}
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buckets</SelectItem>
              <SelectItem value="in_pipeline">In pipeline</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="due_30">Due 30d</SelectItem>
              <SelectItem value="due_60">Due 60d</SelectItem>
              <SelectItem value="due_90">Due 90d</SelectItem>
              <SelectItem value="later">Later</SelectItem>
            </SelectContent>
          </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-500 font-mono text-sm">Loading renewals…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <div className="font-heading text-lg text-slate-700">No renewals in this window</div>
              <div className="text-sm text-slate-500 mt-1">
                Mark deals as "Closed Won" with a term length to see them here.
              </div>
            </div>
          ) : (
            <table className="w-full" data-testid="renewals-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  {isAdmin && (
                    <th className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        onChange={toggleSelectAll}
                        onClick={(e) => e.stopPropagation()}
                        checked={filtered.length > 0 && filtered.every((d) => selected.has(d.id))}
                        aria-label="Select all visible renewals"
                        data-testid="renewals-select-all"
                        className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3">Account · Deal</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Term</th>
                  <th className="px-4 py-3">Closed</th>
                  <th className="px-4 py-3">Renews</th>
                  <th className="px-4 py-3 text-right">In</th>
                  <th className="px-4 py-3 text-right">MRR</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-2 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const b = BUCKET_LABEL[d.bucket] || BUCKET_LABEL.later;
                  return (
                    <tr
                      key={d.id}
                      data-testid={`renewal-row-${d.id}`}
                      className={`border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors ${selected.has(d.id) ? "bg-rose-50/60" : ""}`}
                      onClick={() => nav(`/app/accounts/${d.account_id}`)}
                    >
                      {isAdmin && (
                        <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(d.id)}
                            onChange={(e) => toggleSelect(d.id, e)}
                            aria-label={`Select ${d.name}`}
                            data-testid={`renewal-select-${d.id}`}
                            className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-500">{d.account_name}</div>
                        <div className="font-medium text-slate-900">{d.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border ${b.color}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${b.dot}`} />{b.label}
                        </span>
                        {d.bucket === "in_pipeline" && d.stage && (
                          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1">{d.stage}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 font-mono">{d.term_months || 12} mo</td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {d.bucket === "in_pipeline" ? <span className="text-slate-400 italic">not yet</span> : fmtDate(d.close_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">
                        {d.bucket === "in_pipeline" ? (d.close_date ? <span className="text-slate-700">{fmtDate(d.close_date)} <span className="text-[10px] font-mono uppercase text-slate-400 ml-1">exp.</span></span> : <span className="text-slate-400 italic">no date</span>) : fmtDate(d.end_date)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {d.days_to_renewal === null || d.days_to_renewal === undefined ? (
                          <span className="font-mono text-sm text-slate-400">—</span>
                        ) : (
                          <span className={`font-mono text-sm ${d.days_to_renewal < 0 ? "text-red-700 font-semibold" : d.days_to_renewal <= 30 ? "text-amber-700 font-semibold" : "text-slate-700"}`}>
                            {d.days_to_renewal < 0 ? `${Math.abs(d.days_to_renewal)}d overdue` : `${d.days_to_renewal}d`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-emerald-700 font-semibold">{fmtMoney(d.mrr)}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm text-slate-700">{fmtMoney(d.revenue)}</td>
                      <td className="px-2 py-3 text-right">
                        {isAdmin && (
                          <button
                            onClick={(e) => removeRenewal(d, e)}
                            disabled={deletingId === d.id}
                            className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-rose-600 disabled:opacity-40"
                            title="Delete this renewal (also removes it from the account)"
                            data-testid={`delete-renewal-${d.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {data?.mrr_at_risk > 0 && (
        <div className="flex items-start gap-2 p-4 rounded-md bg-amber-50 border border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">{fmtMoney(data.mrr_at_risk)} MRR</span> is up for renewal in the
            next 90 days. Prioritise these accounts for renewal conversations.
          </div>
        </div>
      )}
    </div>
  );
}

const SummaryCard = ({ label, value, accent = "text-slate-900", testid }) => (
  <Card data-testid={testid} className="border-slate-200">
    <CardContent className="p-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${accent}`}>{value}</div>
    </CardContent>
  </Card>
);
